import * as React from 'react';
import type { IntersectionOptions } from 'react-intersection-observer';
import { useInView } from 'react-intersection-observer';
import styled, { css } from 'styled-components';

import { usePageVisibility } from '@xing-com/crate-hooks-use-page-visibility';
import { DotLoadingIndicator } from '@xing-com/dot-loading-indicator';

import { useDelayedFalse } from './use-delayed-false';

export type LazyLoadingOptions = IntersectionOptions;

export type LazyLoadingProps = {
  /**
   * Indicates if more content is loading. This should be used to skip calling
   * `onLazyLoad` while already fetching more items. The loading indicator is
   * always shown.
   */
  loading?: boolean;
  /**
   * Callback function that is called when the component is in view and
   * `loading` is false. This should be used to load more content.
   *
   * If it is an async callback function should return a promise that resolves when the loading
   * is finished, and we consider it as loading until the promise resolves.
   */
  onLazyLoad: () => void | Promise<void>;
  /**
   * Reduces the height of the loading indicator. This enables the styles as
   * `@xing-com/crate-components-lazy-loader` has before. Leave it off to get
   * the default height of 24px.
   */
  noMargin?: boolean;
  /**
   * Optionally style the `<div>` that wraps the loading indicator.
   */
  className?: string;
  /**
   * Options passed to `react-intersection-observer`.
   * See: https://www.npmjs.com/package/react-intersection-observer#user-content-api
   * And: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#intersection_observer_options
   */
  options?: LazyLoadingOptions;
  ['data-testid']?: string;
  /** @deprecated use `data-testid` instead */
  ['data-qa']?: string;
  /**
   * Same as `options.rootMargin`.
   * @deprecated use `options.rootMargin` instead
   */
  rootMargin?: string;
};

const defaultLoadingIndicator = (
  <DotLoadingIndicator data-testid="lazy-loader-loading-indicator" />
);

const StyledContainer = styled.div<{ $noMargin: boolean }>`
  display: flex;
  justify-content: center;
  min-height: 1px;
  ${({ $noMargin }) =>
    $noMargin
      ? css``
      : css`
          align-items: center;
          height: 24px;
          margin: 48px 0;
        `}
`;

export const LazyLoading: React.FC<
  React.PropsWithChildren<LazyLoadingProps>
> = ({
  loading,
  onLazyLoad,
  noMargin = false,
  children,
  className,
  options,
  'data-testid': dataTestIs,
  rootMargin,
  'data-qa': dataQa,
}) => {
  const isPageVisible = usePageVisibility();
  const onLazyLoadRef = React.useRef(onLazyLoad);
  onLazyLoadRef.current = onLazyLoad;

  const [promiseLoading, setPromiseLoading] = React.useState(false);

  const isLoading = Boolean(loading) || promiseLoading;

  // Delay the end of loading to have a chance to render the new items:
  const delayedLoading = useDelayedFalse(Boolean(isLoading));

  const [ref, inView] = useInView({
    rootMargin,
    threshold: 0.5,
    ...options,
  });

  React.useEffect(() => {
    const hasToLoad = inView && !delayedLoading && isPageVisible;
    if (hasToLoad) {
      const callbackResult = onLazyLoadRef.current();
      if (typeof callbackResult?.finally === 'function') {
        setPromiseLoading(true);
        callbackResult.finally(() => {
          setPromiseLoading(false);
        });
      }
    }
  }, [inView, delayedLoading, isPageVisible]);

  const loadingIndicator = children ?? defaultLoadingIndicator;

  return (
    <StyledContainer
      $noMargin={noMargin}
      className={className}
      ref={ref}
      data-qa={dataQa}
      data-testid={dataTestIs}
    >
      {loadingIndicator}
    </StyledContainer>
  );
};
